import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ListResponse,
  ManualNotifyResponse,
  PushHistoryEntry,
  PushHistoryListParams,
} from '../types';
import { api } from './client';

export interface NotifyInput {
  type?: 'post' | 'page';
  slug: string;
  locale: string;
  delayMinutes?: number;
  /** When true, skip the queue and broadcast inline. */
  immediate?: boolean;
}

export function useTriggerBreakingNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NotifyInput) => api.post<ManualNotifyResponse>('/admin/notify', input),
    // Refresh the history table after a successful broadcast.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pushes'] }),
  });
}

/**
 * Cancel a pending breaking-news push from the History tab. The server
 * idempotently no-ops if the push has already fired or been cancelled —
 * `cancelled: false` in the response means "the scheduler beat us to it,"
 * not "something errored."
 */
export function useCancelBreakingPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true; cancelled: boolean }>(`/admin/pushes/${encodeURIComponent(id)}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pushes'] }),
  });
}

export interface LastBroadcastInfo {
  pendingId: string;
  status: 'pending' | 'sent' | 'cancelled';
  manual: boolean;
  triggerSource: 'manual' | 'category' | 'auto';
  scheduledAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  deliveredCount: number | null;
}

/**
 * Look up the most recent pending_push for a post (slug + locale). Returns
 * null when the post has never been broadcast. Used to surface a
 * "this was already pushed N minutes ago" warning before the editor confirms
 * a re-broadcast.
 */
export function useLastBroadcast(
  slug: string | null,
  locale: string,
  type: 'post' | 'page' = 'post',
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['post-last-broadcast', slug, locale, type] as const,
    enabled: !!slug && (opts?.enabled ?? true),
    // Always pull fresh on dialog open — the editor's deciding based on
    // recency, so a stale cached value defeats the purpose.
    staleTime: 0,
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.get<LastBroadcastInfo | null>(
        `/admin/posts/${encodeURIComponent(slug ?? '')}/last-broadcast`,
        { locale, type },
      ),
  });
}

export function usePushHistory(params: PushHistoryListParams) {
  return useQuery({
    queryKey: ['pushes', params],
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<ListResponse<PushHistoryEntry>>('/admin/pushes', {
        ...params,
        manualOnly: params.manualOnly ? '1' : undefined,
      }),
  });
}

export type AutoBreakingOutcome =
  | 'pushed'
  | 'below_threshold'
  | 'manual_cooldown'
  | 'daily_cap'
  | 'dedup'
  | 'disabled'
  | 'no_post';

export interface AutoBreakingDecisionItem {
  id: string;
  decidedAt: string;
  locale: string;
  score: number;
  threshold: number;
  signals: {
    category: number;
    keyword: number;
    newsDeskEcho: number;
    editorFlagEcho: number;
    llm: number;
    details?: Record<string, string | number | boolean | null>;
  };
  outcome: AutoBreakingOutcome;
  reason: string | null;
  post: { id: string; slug: string; title: string; locale: string } | null;
  pendingPushId: string | null;
}

export interface AutoBreakingDecisionsResponse {
  config: {
    enabled: boolean;
    /** 'admin' if an admin has toggled via UI; 'env' if still env-default. */
    enabledSource: 'admin' | 'env';
    /** ISO string of the most recent admin toggle, or null. */
    enabledUpdatedAt: string | null;
    /** User id of the toggler, or 'admin-token', or null. */
    enabledUpdatedBy: string | null;
    threshold: number;
    dailyCapPerLocale: number;
    manualCooldownMinutes: number;
    llmEnabled: boolean;
    /** True iff llmEnabled AND the API process loaded a non-empty GROQ_API_KEY. */
    llmConfigured: boolean;
    /** Diagnostic — lets editors confirm the API actually sees the key. */
    groqApiKeyPresent: boolean;
    llmModel: string;
    llmBorderline: [number, number];
  };
  cache: { items: number; lastRefreshAt: string | null };
  items: AutoBreakingDecisionItem[];
}

export function useAutoBreakingDecisions(params: {
  locale?: string;
  outcome?: AutoBreakingOutcome;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['auto-breaking-decisions', params],
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<AutoBreakingDecisionsResponse>('/admin/auto-breaking-decisions', {
        ...(params.locale ? { locale: params.locale } : {}),
        ...(params.outcome ? { outcome: params.outcome } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      }),
  });
}

export function useSetAutoBreakingEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<{ enabled: boolean }>('/admin/auto-breaking/settings', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auto-breaking-decisions'] }),
  });
}
